import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Volume2, VolumeX, Loader2, KeyRound } from "lucide-react";
import Logo from "@/components/Logo";
import PSeQ2XrButton from "@/components/PSeQ2XrButton";
import { useLang, useAuth } from "@/App";
import { EASE } from "@/components/motion";
import { playIntroSoundscape } from "@/introSound";

const API = process.env.REACT_APP_BACKEND_URL;
const ROWS = 7;

const inputCls =
  "w-full rounded-xl border border-lined bg-white/80 px-4 py-3 text-sm text-ink outline-none transition-colors duration-300 placeholder:text-ink/30 focus:border-teal";

function AuthCard() {
  const { t } = useLang();
  const { setUser } = useAuth();
  const l = t.landing;
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(false);
    try {
      const body = mode === "signup" ? { name, email, password } : { email, password };
      const r = await fetch(`${API}/api/auth/${mode}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("auth failed");
      const data = await r.json();
      setUser(data.user);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      data-testid="intro-auth-card"
      onClick={(e) => e.stopPropagation()}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 1.55, ease: EASE }}
      className="w-full max-w-sm cursor-default rounded-3xl border border-lined bg-white/85 p-6 shadow-2xl shadow-sky-900/10 backdrop-blur-xl sm:p-7"
    >
      <button
        data-testid="intro-google-btn"
        onClick={googleLogin}
        className="inline-flex w-full items-center justify-center gap-2.5 rounded-full border border-lined bg-white px-6 py-3 font-headline text-sm font-bold text-ink transition-colors duration-300 hover:bg-sky-50"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
          <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z" />
          <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z" />
          <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1z" />
          <path fill="#EA4335" d="M12 4.77c1.76 0 3.35.61 4.6 1.8l3.42-3.42A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.88 8.87 4.77 12 4.77z" />
        </svg>
        {l.google}
      </button>

      <div className="my-5 flex items-center gap-3">
        <span className="h-px flex-1 bg-lined" />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/40">{l.divider}</span>
        <span className="h-px flex-1 bg-lined" />
      </div>

      <form onSubmit={submit} className="flex flex-col gap-3">
        {mode === "signup" && (
          <input
            data-testid="intro-auth-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={l.name}
            required
            minLength={2}
            className={inputCls}
          />
        )}
        <input
          data-testid="intro-auth-email"
          type="text"
          inputMode="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={l.email}
          required
          className={inputCls}
        />
        <input
          data-testid="intro-auth-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={l.password}
          required
          minLength={6}
          className={inputCls}
        />
        {error && (
          <p data-testid="intro-auth-error" className="font-mono text-[10px] leading-relaxed text-red-500">
            {l.err}
          </p>
        )}
        <button
          data-testid="intro-auth-submit"
          type="submit"
          disabled={busy}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-teal px-6 py-3 font-headline text-sm font-bold text-white transition-colors duration-300 hover:bg-deep disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {mode === "signup" ? l.signupBtn : l.loginBtn}
        </button>
      </form>

      <button
        data-testid="intro-auth-switch"
        onClick={() => {
          setMode(mode === "signup" ? "login" : "signup");
          setError(false);
        }}
        className="mt-4 w-full text-center font-mono text-[10px] uppercase tracking-[0.16em] text-ink/50 transition-colors duration-200 hover:text-teal"
      >
        {mode === "signup" ? l.toLogin : l.toSignup}
      </button>
    </motion.div>
  );
}

export default function IntroOverlay({ onFinish }) {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const l = t.landing;
  const [muted, setMuted] = useState(false);
  const [adminBusy, setAdminBusy] = useState(false);
  const stopSound = useRef(null);

  // Quick admin access requested by the owner: one click logs in with the seeded admin account.
  // Pinned bottom-left of the overlay so it stays visible on every screen size.
  const adminLogin = async () => {
    setAdminBusy(true);
    try {
      const r = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: "admin", password: "dekrbat" }),
      });
      if (!r.ok) throw new Error("auth failed");
      const data = await r.json();
      setUser(data.user);
      navigate("/admin");
    } catch {
      // login failed — stay on the landing
    } finally {
      setAdminBusy(false);
    }
  };

  useEffect(() => {
    stopSound.current = playIntroSoundscape();
    document.body.style.overflow = "hidden";
    window.lenis?.stop();
    return () => {
      stopSound.current?.();
      document.body.style.overflow = "";
      window.lenis?.start();
    };
  }, []);

  useEffect(() => {
    if (user) onFinish();
  }, [user, onFinish]);

  const toggleSound = (e) => {
    e.stopPropagation();
    if (!muted) stopSound.current?.();
    setMuted(true);
  };

  return (
    <motion.div
      data-testid="intro-overlay"
      onClick={onFinish}
      exit={{ opacity: 0, scale: 1.08, filter: "blur(8px)", transition: { duration: 0.9, ease: EASE } }}
      className="grain-overlay !fixed inset-0 z-[100] flex cursor-pointer items-center justify-center overflow-hidden bg-gradient-to-br from-sky-100 via-white to-sky-200"
      role="button"
      aria-label={l.visitor}
    >
      <div className="pointer-events-none absolute inset-0 -rotate-12" aria-hidden="true">
        <div className="flex h-[135%] w-[170%] -translate-x-[18%] -translate-y-[12%] flex-col justify-between">
          {Array.from({ length: ROWS }).map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: i % 2 === 0 ? -140 : 140 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1.3, delay: 0.12 + i * 0.07, ease: EASE }}
              className="whitespace-nowrap font-headline text-6xl font-extrabold uppercase tracking-[0.22em] sm:text-7xl lg:text-8xl"
              style={
                i % 2 === 0
                  ? { color: "transparent", WebkitTextStroke: "1.5px rgba(56,189,248,0.3)" }
                  : { color: "rgba(2,132,199,0.05)" }
              }
            >
              {Array(7).fill("DEKRBAT").join(" · ")}
            </motion.div>
          ))}
        </div>
      </div>
      <div className="hero-glow pointer-events-none absolute inset-0" aria-hidden="true" />

      <div className="relative flex w-full max-w-6xl flex-col items-center gap-8 overflow-y-auto px-5 pb-28 pt-10 lg:flex-row lg:items-center lg:justify-between lg:gap-14 lg:pb-10">
        <div className="flex max-w-xl flex-col items-center text-center lg:items-start lg:text-left">
          <motion.div
            data-testid="intro-logo"
            initial={{ scale: 0, rotate: -30, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 16, delay: 0.25 }}
          >
            <Logo markOnly className="h-14 w-auto sm:h-16" />
          </motion.div>

          <motion.div
            initial={{ scale: 2.4, opacity: 0, rotate: -18 }}
            animate={{ scale: 1, opacity: 1, rotate: -8 }}
            transition={{ type: "spring", stiffness: 320, damping: 16, delay: 0.65 }}
            className="mt-5 border-[3px] border-teal/90 px-6 py-2 sm:px-8 sm:py-2.5"
            style={{ boxShadow: "inset 0 0 0 1px rgba(2,132,199,0.25)" }}
          >
            <span className="block text-center font-headline text-xl font-extrabold uppercase tracking-[0.35em] text-teal sm:text-2xl">
              Dekrbat
            </span>
            <span className="mt-0.5 block text-center font-mono text-[9px] uppercase tracking-[0.6em] text-teal/80 sm:text-[10px]">
              Pure
            </span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.05 }}
            className="mt-7 font-mono text-[10px] uppercase tracking-[0.4em] text-teal sm:text-[11px]"
          >
            {l.overline}
          </motion.p>

          <motion.h1
            data-testid="intro-landing-title"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.2, ease: EASE }}
            className="mt-4 font-headline text-3xl font-extrabold leading-[1.1] tracking-tight text-ink sm:text-4xl"
          >
            {l.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.35, ease: EASE }}
            className="mt-5 max-w-lg text-sm leading-relaxed text-ink/70 sm:text-base"
          >
            {l.sub}
          </motion.p>

          <motion.ul
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.5, ease: EASE }}
            className="mt-6 flex flex-col items-center gap-2.5 lg:items-start"
          >
            {l.points.map((pt, i) => (
              <li key={i} className="flex items-center gap-2.5 font-mono text-[11px] tracking-[0.08em] text-ink/75 sm:text-xs">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-aqua" style={{ boxShadow: "0 0 8px rgba(56,189,248,0.8)" }} />
                {pt}
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.9, ease: EASE }}
            className="mt-6 w-full max-w-sm"
          >
            <PSeQ2XrButton />
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.1 }}
            className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40"
          >
            — {l.visitor} —
          </motion.p>
        </div>

        <AuthCard />
      </div>

      <motion.button
        data-testid="intro-admin-btn"
        onClick={(e) => {
          e.stopPropagation();
          adminLogin();
        }}
        disabled={adminBusy}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white/70 px-4 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink/50 backdrop-blur-md transition-colors duration-300 hover:border-teal hover:text-teal disabled:opacity-50 sm:bottom-7 sm:left-7 sm:top-auto"
        aria-label="Admin"
      >
        {adminBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
        Admin
      </motion.button>

      <motion.button
        data-testid="intro-sound-toggle"
        onClick={toggleSound}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-7 right-7 rounded-full border border-ink/15 bg-white/70 p-2.5 text-ink/50 backdrop-blur-md transition-colors duration-300 hover:border-teal hover:text-teal"
        aria-label={muted ? t.intro.soundOff : t.intro.soundOn}
      >
        {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
      </motion.button>
    </motion.div>
  );
}
